'use client';

import Input from '@/components/atoms/Input';
import { ITrendingWords } from '@/interfaces/search';
import {
  getTrendingWords,
  requestSearch,
} from '@/services/apis/modules/search';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { PATH_URL } from '@/statics/static';
import { updateURLWhenSearch } from '@/utils/funtion';
import { IIndicatorDataSearch } from '@/interfaces/indicator';
import Select from '@/components/atoms/select';
import AutoComplete from '@/components/atoms/autoComplete';
import { typeSearch } from '@/statics/data';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '@/components/atoms/icon';
import { FaCaretDown } from 'react-icons/fa6';
import Skeleton from '@/components/atoms/skeletons';
import Empty from '@/components/atoms/empty';
import { setDetailUrl } from '@/stores/modules/detailUrl';

interface SearchEngineProps {
  children?: React.ReactNode;
  hasTrendingWords?: boolean;
  hasSelect?: boolean;
  triggerSearch?: () => void;
}

const SearchEngine: React.FC<SearchEngineProps> = ({
  hasTrendingWords = true,
  hasSelect,
  triggerSearch,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pathname = usePathname();
  const userData = useSelector((state: any) => state.userData);
  const router = useRouter();
  const searchParams = useSearchParams();
  const [trendingWords, setTrendingWords] = useState<ITrendingWords[]>([]);
  const [suggestions, setSuggestions] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState({
    input: '',
    type: 'all',
    isFocused: false,
  });
  const dispatch = useDispatch();

  const fetchTrendingWords = useCallback(async (limit: number) => {
    try {
      const data: any = await getTrendingWords(limit);
      if (data.length > 0) {
        setTrendingWords(data);
      }
    } catch (error) {
      console.error('Failed to fetch trending words:', error);
    }
  }, []);

  useEffect(() => {
    if (trendingWords === undefined) {
      fetchTrendingWords(6);
    }
  }, []);

  useEffect(() => {
    const updateSearchDataFromURL = () => {
      const searchKeyword = searchParams?.get('tim-kiem') || '';
      const searchType =
        pathname === PATH_URL.ARTICLE_URL
          ? 'articles'
          : searchParams?.get('type') || 'all';

      setSearchData({
        ...searchData,
        input: searchKeyword,
        type: searchType,
      });
    };

    updateSearchDataFromURL();
  }, [pathname, searchParams]);

  const handleGetSuggesions = async (keyword: string) => {
    if (!keyword.trim()) return;
    try {
      setLoading(true);
      const response: IIndicatorDataSearch = await requestSearch({
        keyword,
        type_search: searchData.type,
        pageIndex: 1,
        pageSize: 10,
      });
      const newSuggestions = [
        ...response.indicator.records.map((item) => ({
          ...item,
          type: 'indicator',
        })),
        ...response.industryIndicator.records.map((item) => ({
          ...item,
          type: 'industry_indicator',
        })),
        ...response.articles.records.map((item) => ({
          ...item,
          type: 'articles',
        })),
        ...response.local_indicator.records.map((item) => ({
          ...item,
          type: 'local_indicator',
        })),
      ];
      setSuggestions(newSuggestions);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchData((prev) => ({ ...prev, input: e.target.value }));
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (!e.target.value.trim()) {
      setSuggestions([]);
      setLoading(false);
      return;
    } else {
      setLoading(true);
      timeoutRef.current = setTimeout(() => {
        handleGetSuggesions(e.target.value);
      }, 1000);
    }
  };

  const handleSearch = () => {
    if (triggerSearch) triggerSearch();
    if (!searchData.input) return;
    const basePath =
      searchData.type === 'articles'
        ? PATH_URL.ARTICLE_URL
        : PATH_URL.SEARCH_URL;
    const url = `${basePath}?tim-kiem=${searchData.input}&type=${searchData.type}`;
    if (pathname === basePath) {
      updateURLWhenSearch(searchData.input, 'tim-kiem');
      updateURLWhenSearch(searchData.type, 'type');
    } else {
      router.push(url);
    }
  };

  const handleChangeType = (value: any) => {
    setSearchData({ ...searchData, type: value.value });
  };

  return (
    <div className="t-search_engine">
      <label
        style={{
          background: 'rgba(255,255,255, 0.9)',
          opacity: 0.8,
          width: '100%',
          marginBottom: 3,
          fontSize: 13,
          fontStyle: 'italic',
          color: '#f00',
          borderRadius: 4,
          padding: '1px 4px',
        }}
      >
        Lưu ý: Sử dụng dấu ngoặc kép &quot; &quot; khi muốn tìm kiếm chính xác cụm từ
      </label>
      <AutoComplete
        options={
          loading
            ? [
                {
                  value: 'loading',
                  label: (
                    <Skeleton
                      active
                      type="default"
                      loading
                      paragraph={{ rows: 3, width: '100%' }}
                    />
                  ),
                },
              ]
            : !suggestions?.length
              ? [
                  {
                    value: 'empty',
                    label: (
                      <div style={{ transform: 'scale(0.8)' }}>
                        <Empty />
                      </div>
                    ),
                  },
                ]
              : [
                  ...suggestions.map((item: any) => ({
                    value: item?.title || item?.name,
                    label: (
                      <a
                        href={
                          item?.title
                            ? `${PATH_URL.ARTICLE_URL}/${item?.slug}?id=${item.id}&description=${item.description}&title=${item.title}`
                            : `/thong-tin-chi-tiet/${item.slug}/${item.id}?type=${item.type}&structure=${item?.is_structure}&isObjectOneTime=${item.is_object_one_time}`
                        }
                      >
                        {item?.title || item?.name}
                      </a>
                    ),
                  })),

                  ...(suggestions?.length > 19
                    ? [
                        {
                          value: 'see-more',
                          label: (
                            <div
                              onClick={() => {
                                handleSearch();
                              }}
                              className="t-search_engine_see-more"
                            >
                              Xem thêm
                            </div>
                          ),
                        },
                      ]
                    : []),
                ]
        }
        style={{ width: '100%' }}
        onFocus={() => {
          setSearchData({
            ...searchData,
            isFocused: true,
          });
        }}
        onBlur={() => {
          setSearchData({
            ...searchData,
            isFocused: false,
          });
        }}
        open={searchData.isFocused && searchData.input.trim() !== ''}
      >
        <div className="t-search_engine_input">
          <Input
            type="search"
            enterButton="Tìm kiếm"
            placeholder="Hãy cho chúng tôi biết bạn đang tìm kiếm dữ liệu gì?..."
            value={searchData.input}
            onChange={handleInputChange}
            onPressEnter={handleSearch}
            onSearch={handleSearch}
          />

          {hasSelect && (
            <Select
              onlyIcon
              defaultValue="all"
              value={searchData.type}
              className="select-in-search"
              onChange={handleChangeType}
              style={{ width: 60 }}
              placeholder="Lựa chọn"
              options={typeSearch}
              popupClassName="select-in-search-dropdown"
              withIcon
              suffixIcon={
                <Icon
                  className="select-in-search-dropdown-icon"
                  component={<FaCaretDown />}
                />
              }
            />
          )}
        </div>
      </AutoComplete>

      {!!trendingWords && hasTrendingWords && (
        <ul className="t-search_engine_recent">
          {trendingWords.map((i, index) => (
            <li key={index}>
              <p>{i?.keyword}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchEngine;
